export const SANDBOX_EMAIL = 'sandbox@test.local'
export const SANDBOX_PASSWORD = 'sandbox'
export const SANDBOX_SPEED_KEY = 'cdk_sandbox_speed_v2'
export const SANDBOX_INSTANT_ELAPSED_S = 10_000
export const SANDBOX_RUNS_KEY = 'cdk_sandbox_runs_v1'

export type SandboxId =
  | 'idle-preview'
  | 'slow-bind'
  | 'poll-404'
  | 'stale-fail'
  | 'preflight-timeout'
  | 'account-switch'
  | 'satisfied'
  | 'account-busy'
  | 'review'
  | 'declined'
  | 'already-used'
  | 'quick-ok'

export type SandboxScenario = {
  id: SandboxId
  code: string
  title: string
  why: string
  expect: string
  plan: string
}

export type SandboxLookup = {
  status: string
  used: boolean
  plan: string
  account_email: string
  used_at: string
  message?: string
}

export type SandboxResult = {
  status: string
  processing: boolean
  stage: string
  stage_message: string
  message: string
  notice?: string
  code?: number
  redemption_token: string
  public_code: string
  cdk_code: string
  plan: string
  account_email: string
}

export type SandboxSubmit = {
  delayMs: number
  http: number
  body: Record<string, unknown>
}

export type SandboxSnapshot = {
  scenario: SandboxScenario
  elapsedSec: number
  submitted: boolean
  lookup: SandboxLookup
  result: SandboxResult
  resultHttp: number
  previewHttp: number
  preview: Record<string, unknown>
  submit: SandboxSubmit
}

export type SandboxRun = {
  submittedAt: number | null
  previewedAt: number
  pollCount: number
}

type TimelineFn = (ctx: {
  elapsedSec: number
  submitted: boolean
  pollCount: number
  token: string
  code: string
  plan: string
  email: string
}) => {
  lookup: Omit<SandboxLookup, 'plan' | 'account_email'> & { account_email?: string }
  result: Partial<SandboxResult> & { status: string }
  resultHttp?: number
  submit: SandboxSubmit
}

const EMAIL = 'ylu@oncogenerix.com'

export const SANDBOX_SCENARIOS: SandboxScenario[] = [
  {
    id: 'idle-preview',
    code: 'PLUS-SB01-IDLE-PREV-0001',
    title: '未使用 · 只预览过',
    why: '有 leftover preview token，从未点开通。',
    expect: '卡密查询 / 进度查询都是未使用，不要显示处理中。',
    plan: 'plus',
  },
  {
    id: 'slow-bind',
    code: 'PLUS-SB02-BIND-SLOW-0001',
    title: '提卡慢 · PLUS-GVJ9 绑池',
    why: '点开通后约 51s 还在绑池，短码仍 unused；20s 软受理后前端会误判失败。',
    expect: '20s 后仍应是正在开通，队列也是处理中；约 90s 开通完成。',
    plan: 'plus',
  },
  {
    id: 'poll-404',
    code: 'PLUS-SB03-POLL-N404-0001',
    title: '受理后卡台 404',
    why: 'redeem 已受理，轮询「兑换结果不存在」；连打 5 次后后端还会写成失败。',
    expect: '受理后 404 应继续等，不要显示失败；约 48s 开通完成。',
    plan: 'plus',
  },
  {
    id: 'stale-fail',
    code: 'PLUS-SB04-OLD-FAIL-0001',
    title: '旧失败盖住新单',
    why: '同一短码早上 failed_precharge，晚上再兑，结果接口仍吐旧失败。',
    expect: '结果接口仍吐旧失败时，页面会整页失败（前后一致）；约 70s 后才完成。',
    plan: 'plus',
  },
  {
    id: 'preflight-timeout',
    code: 'PLUS-SB05-PRE-TIME-0001',
    title: '预检真超时回池',
    why: '预检 HTTP 超时，卡密未消耗，回池。',
    expect: '预检超时回池：开通准备中，卡密还在。不要失败和「处理中」叠在一起。',
    plan: 'plus',
  },
  {
    id: 'account-switch',
    code: 'PLUS-SB06-NEED-SWAP-0001',
    title: '账号不支持 / 要换号',
    why: 'captcha / not eligible。',
    expect: '请更换账号，卡密未消耗。',
    plan: 'plus',
  },
  {
    id: 'satisfied',
    code: 'PLUS-SB07-HAS-PLAN-0001',
    title: '套餐已满足',
    why: 'already_satisfied。',
    expect: '当前套餐已满足，卡密未消耗。',
    plan: 'plus',
  },
  {
    id: 'account-busy',
    code: 'PLUS-SB08-ACCT-BUSY-0001',
    title: '账号已有开通中订单',
    why: '同一邮箱正在直充。',
    expect: '应提示等第一张到账。现在进度查询常合成「订单失败」。',
    plan: 'plus',
  },
  {
    id: 'review',
    code: 'PLUS-SB09-REVW-HOLD-0001',
    title: '待对账 review',
    why: '官方禁止重试。',
    expect: '停在待对账，不要再点开通。',
    plan: 'plus',
  },
  {
    id: 'declined',
    code: 'PLUS-SB10-DECL-FAIL-0001',
    title: '真失败 declined',
    why: '卡台明确拒绝。',
    expect: '本次没有开通成功。',
    plan: 'plus',
  },
  {
    id: 'already-used',
    code: 'PLUS-SB11-USED-OK-0001',
    title: '已经核销',
    why: 'consumed + webhook 完成。',
    expect: '卡密已使用 / 开通完成。',
    plan: 'plus',
  },
  {
    id: 'quick-ok',
    code: 'PLUS-SB12-QUICK-OK-0001',
    title: '正常开通',
    why: '对照用：提交后几秒完成。',
    expect: '开通完成。',
    plan: 'plus',
  },
]

const BY_ID = Object.fromEntries(SANDBOX_SCENARIOS.map((s) => [s.id, s])) as Record<
  SandboxId,
  SandboxScenario
>

export function parseSandboxCode(raw: string): SandboxScenario | null {
  const code = String(raw || '')
    .trim()
    .toUpperCase()
  if (!code) return null
  const hit = SANDBOX_SCENARIOS.find((s) => s.code === code)
  if (hit) return hit
  const m = code.match(/^(PLUS|PRO20X|PRO5X)-SB(\d{2})\b/)
  if (!m) return null
  const idx = Number(m[2])
  return SANDBOX_SCENARIOS[idx - 1] || null
}

export function isSandboxCode(raw: string) {
  return !!parseSandboxCode(raw)
}

export function sandboxToken(code: string) {
  return `pub-sb-${String(code || '').trim().toUpperCase()}`
}

export function codeFromSandboxToken(token: string) {
  const t = String(token || '').trim()
  if (!t.startsWith('pub-sb-')) return ''
  return t.slice('pub-sb-'.length)
}

export function readSandboxSpeed() {
  try {
    const raw = sessionStorage.getItem(SANDBOX_SPEED_KEY)
    if (raw == null) return 0
    const n = Number(raw)
    if (n === 0 || n === 1 || n === 3 || n === 10) return n
  } catch {
    /* ignore */
  }
  return 0
}

export function writeSandboxSpeed(speed: number) {
  const n = speed === 1 || speed === 3 || speed === 10 ? speed : 0
  try {
    sessionStorage.setItem(SANDBOX_SPEED_KEY, String(n))
  } catch {
    /* ignore */
  }
  return n
}

export function isSandboxInstant(speed = readSandboxSpeed()) {
  return speed === 0
}

function readRuns(): Record<string, SandboxRun> {
  try {
    const raw = sessionStorage.getItem(SANDBOX_RUNS_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeRuns(runs: Record<string, SandboxRun>) {
  try {
    sessionStorage.setItem(SANDBOX_RUNS_KEY, JSON.stringify(runs))
  } catch {
    /* ignore */
  }
}

export function getSandboxRun(code: string): SandboxRun {
  const key = String(code || '').trim().toUpperCase()
  return readRuns()[key] || { submittedAt: null, previewedAt: 0, pollCount: 0 }
}

export function markSandboxPreview(code: string) {
  const key = String(code || '').trim().toUpperCase()
  const runs = readRuns()
  const prev = runs[key] || { submittedAt: null, previewedAt: 0, pollCount: 0 }
  runs[key] = { ...prev, previewedAt: prev.previewedAt || Date.now() }
  writeRuns(runs)
}

export function markSandboxSubmit(code: string) {
  const key = String(code || '').trim().toUpperCase()
  const runs = readRuns()
  const prev = runs[key] || { submittedAt: null, previewedAt: Date.now(), pollCount: 0 }
  runs[key] = { ...prev, submittedAt: prev.submittedAt || Date.now() }
  writeRuns(runs)
}

export function bumpSandboxPoll(code: string) {
  const key = String(code || '').trim().toUpperCase()
  const runs = readRuns()
  const prev = runs[key] || { submittedAt: null, previewedAt: Date.now(), pollCount: 0 }
  runs[key] = { ...prev, pollCount: (prev.pollCount || 0) + 1 }
  writeRuns(runs)
  return runs[key].pollCount
}

export function resetSandboxRun(code: string) {
  const key = String(code || '').trim().toUpperCase()
  const runs = readRuns()
  delete runs[key]
  writeRuns(runs)
}

export function resetAllSandboxRuns() {
  writeRuns({})
}

function usedAt(ts: number) {
  return new Date(ts).toISOString().replace('T', ' ').slice(0, 19)
}

function baseResult(code: string, plan: string, token: string, email = EMAIL): SandboxResult {
  return {
    status: 'unused',
    processing: false,
    stage: 'unused',
    stage_message: '尚未兑换',
    message: '',
    redemption_token: token,
    public_code: code,
    cdk_code: code,
    plan,
    account_email: email,
  }
}

const TIMELINES: Record<SandboxId, TimelineFn> = {
  'idle-preview': ({ token, code, plan }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: { ...baseResult(code, plan, token, ''), status: 'unused', processing: false, stage: 'unused' },
    submit: {
      delayMs: 400,
      http: 200,
      body: { status: 'unused', processing: false, redemption_token: token, public_code: code },
    },
  }),
  'slow-bind': ({ elapsedSec, submitted, token, code, plan, email }) => {
    if (!submitted || elapsedSec < 51) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: {
          ...baseResult(code, plan, token, ''),
          status: 'unused',
          processing: false,
          stage: 'allocating',
          stage_message: '正在提卡分配资源',
        },
        submit: {
          delayMs: 51_000,
          http: 200,
          body: { status: 'processing', processing: true, stage: 'redeem_submit', redemption_token: token, public_code: code },
        },
      }
    }
    if (elapsedSec < 90) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: {
          ...baseResult(code, plan, token, email),
          status: 'unused',
          processing: false,
          stage: 'poll',
          message: '兑换结果不存在',
          stage_message: '兑换结果不存在',
          notice: 'failed',
          code: 404,
        },
        submit: {
          delayMs: 51_000,
          http: 200,
          body: { status: 'queued', processing: true, redemption_token: token, public_code: code, account_email: email },
        },
      }
    }
    return {
      lookup: { status: 'used', used: true, used_at: usedAt(Date.now()), message: '卡密已使用' },
      result: {
        ...baseResult(code, plan, token, email),
        status: 'completed',
        processing: false,
        stage: 'completed',
        message: '开通完成',
        notice: 'done',
      },
      submit: {
        delayMs: 51_000,
        http: 200,
        body: { status: 'completed', processing: false, redemption_token: token, public_code: code, account_email: email },
      },
    }
  },
  'poll-404': ({ elapsedSec, submitted, pollCount, token, code, plan, email }) => {
    if (!submitted) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: { ...baseResult(code, plan, token, ''), status: 'unused', processing: false, stage: 'unused' },
        submit: {
          delayMs: 1_200,
          http: 202,
          body: { status: 'queued', processing: true, redemption_token: token, public_code: code, account_email: email },
        },
      }
    }
    if (elapsedSec < 48) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: {
          ...baseResult(code, plan, token, email),
          status: '',
          processing: true,
          stage: 'poll',
          message: '兑换结果不存在',
          stage_message: '兑换结果不存在',
          notice: 'failed',
          code: 404,
        },
        submit: {
          delayMs: 1_200,
          http: 202,
          body: { status: 'queued', processing: true, redemption_token: token, public_code: code, account_email: email },
        },
      }
    }
    return {
      lookup: { status: 'used', used: true, used_at: usedAt(Date.now()), message: '卡密已使用' },
      result: {
        ...baseResult(code, plan, token, email),
        status: 'completed',
        processing: false,
        stage: 'completed',
        message: '开通完成',
        notice: 'done',
      },
      submit: {
        delayMs: 1_200,
        http: 202,
        body: { status: 'completed', processing: false, redemption_token: token, public_code: code, account_email: email },
      },
    }
  },
  'stale-fail': ({ elapsedSec, submitted, token, code, plan, email }) => {
    if (!submitted) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: { ...baseResult(code, plan, token, ''), status: 'unused', processing: false, stage: 'unused' },
        submit: {
          delayMs: 800,
          http: 200,
          body: {
            status: 'processing',
            processing: true,
            redemption_token: token,
            public_code: code,
            last_error: 'failed_precharge',
          },
        },
      }
    }
    if (elapsedSec < 70) {
      return {
        lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
        result: {
          ...baseResult(code, plan, token, email),
          status: 'failed_precharge',
          processing: false,
          stage: 'failed_precharge',
          message: '上游预扣失败，开通未成功',
          notice: 'failed',
        },
        submit: {
          delayMs: 800,
          http: 200,
          body: {
            status: 'processing',
            processing: true,
            redemption_token: token,
            public_code: code,
            last_error: 'failed_precharge',
          },
        },
      }
    }
    return {
      lookup: { status: 'used', used: true, used_at: usedAt(Date.now()), message: '卡密已使用' },
      result: {
        ...baseResult(code, plan, token, email),
        status: 'completed',
        processing: false,
        stage: 'completed',
        message: '开通完成',
        notice: 'done',
      },
      submit: {
        delayMs: 800,
        http: 200,
        body: { status: 'completed', processing: false, redemption_token: token, public_code: code, account_email: email },
      },
    }
  },
  'preflight-timeout': ({ submitted, token, code, plan }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, ''),
      status: submitted ? 'failed' : 'unused',
      processing: false,
      stage: 'preflight',
      message: submitted ? '预检超时，卡密未消耗，请重新兑换' : '',
      notice: submitted ? 'failed' : undefined,
    },
    submit: {
      delayMs: 45_000,
      http: 502,
      body: {
        status: 'failed',
        notice: 'failed',
        error: '预检超时，卡密未消耗，请重新兑换',
        message: '预检超时，卡密未消耗，请重新兑换',
        processing: false,
        public_code: code,
      },
    },
  }),
  'account-switch': ({ token, code, plan }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, ''),
      status: 'failed_precharge',
      processing: false,
      message: '账号异常，请更换账号后重试',
      notice: 'failed',
    },
    submit: {
      delayMs: 1_200,
      http: 200,
      body: {
        status: 'failed_precharge',
        notice: 'failed',
        message: '账号异常，请更换账号后重试',
        processing: false,
        redemption_token: token,
      },
    },
  }),
  'satisfied': ({ token, code, plan, email }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, email),
      status: 'already_satisfied',
      processing: false,
      message: '当前账号已有同等或更高套餐，无需重复开通',
      notice: 'satisfied',
    },
    submit: {
      delayMs: 1_000,
      http: 200,
      body: {
        status: 'already_satisfied',
        notice: 'satisfied',
        message: '当前账号已有同等或更高套餐，无需重复开通',
        processing: false,
        redemption_token: token,
        account_email: email,
      },
    },
  }),
  'account-busy': ({ token, code, plan, email }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, email),
      status: 'failed',
      processing: false,
      message: '该账号已有开通中的订单，请等第一张到账后再兑。卡密未消耗。',
      notice: 'account_busy',
    },
    submit: {
      delayMs: 700,
      http: 200,
      body: {
        status: 'failed',
        notice: 'account_busy',
        message: '该账号已有开通中的订单，请等第一张到账后再兑。卡密未消耗。',
        processing: false,
        redemption_token: token,
        account_email: email,
      },
    },
  }),
  'review': ({ submitted, token, code, plan, email }) => ({
    lookup: submitted
      ? { status: 'processing', used: false, used_at: '', message: '卡密兑换中' }
      : { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, email),
      status: submitted ? 'review' : 'unused',
      processing: submitted,
      stage: submitted ? 'manual_review' : 'unused',
      message: submitted ? '待人工复核' : '',
    },
    submit: {
      delayMs: 900,
      http: 202,
      body: { status: 'review', processing: true, redemption_token: token, public_code: code, account_email: email },
    },
  }),
  declined: ({ submitted, token, code, plan }) => ({
    lookup: { status: 'unused', used: false, used_at: '', message: '卡密未使用' },
    result: {
      ...baseResult(code, plan, token, ''),
      status: submitted ? 'declined' : 'unused',
      processing: false,
      message: submitted ? '本次没有开通成功，卡密尚未消耗' : '',
      notice: submitted ? 'failed' : undefined,
    },
    submit: {
      delayMs: 1_400,
      http: 200,
      body: {
        status: 'declined',
        notice: 'failed',
        message: '本次没有开通成功，卡密尚未消耗',
        processing: false,
        redemption_token: token,
      },
    },
  }),
  'already-used': ({ token, code, plan, email }) => ({
    lookup: { status: 'used', used: true, used_at: usedAt(Date.now()), message: '卡密已使用', account_email: email },
    result: {
      ...baseResult(code, plan, token, email),
      status: 'completed',
      processing: false,
      stage: 'completed',
      message: '开通完成',
      notice: 'done',
    },
    resultHttp: 200,
    submit: {
      delayMs: 200,
      http: 422,
      body: { error: '卡密已使用', message: '卡密已使用', notice: 'used' },
    },
  }),
  'quick-ok': ({ elapsedSec, submitted, token, code, plan, email }) => {
    const done = submitted && elapsedSec >= 4
    return {
      lookup: done
        ? { status: 'used', used: true, used_at: usedAt(Date.now()), message: '卡密已使用' }
        : { status: submitted ? 'processing' : 'unused', used: false, used_at: '', message: submitted ? '卡密兑换中' : '卡密未使用' },
      result: {
        ...baseResult(code, plan, token, email),
        status: done ? 'completed' : submitted ? 'queued' : 'unused',
        processing: submitted && !done,
        stage: done ? 'completed' : submitted ? 'poll' : 'unused',
        message: done ? '开通完成' : '',
        notice: done ? 'done' : undefined,
      },
      submit: {
        delayMs: 1_200,
        http: 202,
        body: { status: 'queued', processing: true, redemption_token: token, public_code: code, account_email: email },
      },
    }
  },
}

export function sandboxElapsedSec(submittedAt: number | null, now: number, speed: number) {
  if (!submittedAt) return 0
  if (speed === 0) return SANDBOX_INSTANT_ELAPSED_S
  return Math.max(0, ((now - submittedAt) * speed) / 1000)
}

export function buildSandboxSnapshot(input: {
  code: string
  now?: number
  speed?: number
  run?: SandboxRun
}): SandboxSnapshot | null {
  const scenario = parseSandboxCode(input.code)
  if (!scenario) return null
  const code = scenario.code
  const run = input.run || getSandboxRun(input.code)
  const speed = input.speed ?? readSandboxSpeed()
  const now = input.now ?? Date.now()
  const elapsedSec = sandboxElapsedSec(run.submittedAt, now, speed)
  const token = sandboxToken(code)
  const plan = scenario.plan
  const raw = TIMELINES[scenario.id]({
    elapsedSec,
    submitted: !!run.submittedAt,
    pollCount: run.pollCount || 0,
    token,
    code,
    plan,
    email: EMAIL,
  })
  const previewOk = scenario.id !== 'already-used'
  return {
    scenario,
    elapsedSec,
    submitted: !!run.submittedAt,
    lookup: {
      status: raw.lookup.status,
      used: raw.lookup.used,
      plan,
      account_email: raw.lookup.account_email || (raw.lookup.used ? EMAIL : ''),
      used_at: raw.lookup.used_at,
      message: raw.lookup.message,
    },
    result: {
      ...baseResult(code, plan, token, raw.result.account_email ?? EMAIL),
      ...raw.result,
      redemption_token: token,
      public_code: code,
      cdk_code: code,
      plan,
    },
    resultHttp: raw.resultHttp ?? 200,
    previewHttp: previewOk ? 200 : 422,
    preview: previewOk
      ? {
          redemption_token: token,
          plan,
          plan_type: plan === 'plus' ? 'Plus' : plan,
          public_code: code,
          message: '卡密有效，可以兑换',
        }
      : { error: '卡密已使用', message: '卡密已使用', notice: 'used' },
    submit: {
      ...raw.submit,
      delayMs: speed === 0 ? 80 : Math.max(80, Math.round(raw.submit.delayMs / speed)),
    },
  }
}

export { BY_ID as sandboxScenarioById }
